import { Controller } from '@hotwired/stimulus';
import Swal from 'sweetalert2';

/**
 * Warns with SweetAlert when navigating away with unsaved changes.
 *
 * Strategy: intercept clicks on any <a href> in the document. If the form is
 * dirty, prevent the navigation and ask the user via SweetAlert.
 *
 * Usage: add `data-controller="dirty-form"` to the <form> element.
 */
export default class extends Controller<HTMLFormElement> {

    private isDirty = false;
    private boundOnClick?: (e: MouseEvent) => void;

    connect(): void {
        this.element.addEventListener('input', this.markDirty);
        this.element.addEventListener('change', this.markDirty);
        this.element.addEventListener('submit', this.markClean, { capture: true });

        this.boundOnClick = this.onAnchorClick.bind(this);
        document.addEventListener('click', this.boundOnClick, { capture: true });
    }

    disconnect(): void {
        this.element.removeEventListener('input', this.markDirty);
        this.element.removeEventListener('change', this.markDirty);
        this.element.removeEventListener('submit', this.markClean, { capture: true });
        if (this.boundOnClick) {
            document.removeEventListener('click', this.boundOnClick, { capture: true });
        }
    }

    private markDirty = (event: Event): void => {
        // Ignore synthetic events fired by other Stimulus controllers (datepicker
        // formatting, currency-input formatting, TomSelect init, etc.). Only real
        // user keystrokes/clicks have `isTrusted === true`.
        if (!event.isTrusted) return;
        this.isDirty = true;
    };

    private markClean = (): void => {
        this.isDirty = false;
    };

    private onAnchorClick(event: MouseEvent): void {
        if (!this.isDirty) return;

        const anchor = (event.target as HTMLElement | null)?.closest('a') as HTMLAnchorElement | null;
        if (!anchor) return;
        if (!anchor.href) return;

        // Skip non-navigational links.
        if (anchor.target === '_blank') return;
        if (anchor.getAttribute('href')?.startsWith('#')) return;
        if (anchor.hasAttribute('data-bs-toggle')) return;
        if (anchor.dataset['dirtyFormSkip'] === 'true') return;

        // Skip modified clicks (cmd/ctrl/shift/meta → new tab/window).
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;

        // Same-anchor navigation (just hash change in same page).
        if (anchor.origin === window.location.origin
            && anchor.pathname === window.location.pathname
            && anchor.search === window.location.search) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const targetUrl = anchor.href;

        void Swal.fire({
            icon: 'warning',
            title: '¿Salir sin guardar?',
            text: 'Tienes cambios sin guardar que se perderán.',
            showCancelButton: true,
            confirmButtonText: 'Salir sin guardar',
            cancelButtonText: 'Quedarme aquí',
            confirmButtonColor: '#dc3545',
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.isDirty = false;
                window.location.href = targetUrl;
            }
        });
    }
}
